import classnames from 'classnames';
import { type CSSProperties, type ReactNode } from 'react';
import styled from 'styled-components';

import { Vertical } from '#src/atoms/layout/flex/index.tsx';

type ShieldShapeOrientation = 'up' | 'down';
type ShieldShapeSize = 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

const ORIENTATION_ROTATION: Record<ShieldShapeOrientation, number> = {
  up: 0,
  down: 180,
};

const SIZE_PX: Record<ShieldShapeSize, number> = {
  xxs: 32,
  xs: 44,
  sm: 54,
  md: 64,
  lg: 80,
  xl: 96,
};

const Root = styled.div`
  position: relative;
  flex-shrink: 0;
  width: var(--shape-size);
  height: var(--shape-size);
  transform: rotate(var(--rotation));
  background: var(--background);
  clip-path: polygon(
    57.36% 1%,
    99% 80.48%,
    89.85% 99%,
    10.07% 99%,
    1% 80.29%,
    42.44% 1%
  );
  &.shield-bottom-glow:after {
    position: absolute;
    inset: 0;
    opacity: 0;
    content: '';
    transition: 0.3s ease opacity;
    background: radial-gradient(
      50% 50% at 50% 100%,
      var(--border),
      transparent 100%
    );
  }
  &.shield-bottom-glow:hover:after {
    opacity: 1;
  }
  @supports (clip-path: shape(from 0% 0%, line to 100% 0%, close)) {
    /* stylelint-disable-next-line declaration-property-value-no-unknown */
    clip-path: shape(
      from 53.36% 1%,
      curve to 59.22% 4.54% with 57.36% 1%,
      line to 97.14% 76.94%,
      curve to 97.23% 84.07% with 99% 80.48%,
      line to 91.63% 95.41%,
      curve to 85.85% 99% with 89.85% 99%,
      line to 14.07% 99%,
      curve to 8.33% 95.4% with 10.07% 99%,
      line to 2.75% 83.89%,
      curve to 2.85% 76.75% with 1% 80.29%,
      line to 40.58% 4.55%,
      curve to 46.44% 1% with 42.44% 1%,
      close
    );
  }
`;

const Inner = styled.div`
  position: absolute;
  inset: 0;
  background: var(--border);
  clip-path: polygon(
    evenodd,
    0% 0%,
    100% 0%,
    100% 100%,
    0% 100%,
    0% 0%,
    56.46% 2.5%,
    97.32% 80.5%,
    88.92% 97.5%,
    11.01% 97.5%,
    2.68% 80.32%,
    43.35% 2.5%,
    56.46% 2.5%
  );
  @supports (clip-path: shape(from 0% 0%, line to 100% 0%, close)) {
    /* stylelint-disable-next-line declaration-property-value-no-unknown */
    clip-path: shape(
      evenodd from 0% 0%,
      line to 100% 0%,
      line to 100% 100%,
      line to 0% 100%,
      close,
      move to 53.36% 2.5%,
      curve to 57.89% 5.24% with 56.46% 2.5%,
      line to 95.82% 77.63%,
      curve to 95.88% 83.4% with 97.32% 80.5%,
      line to 90.28% 94.75%,
      curve to 85.85% 97.5% with 88.92% 97.5%,
      line to 14.07% 97.5%,
      curve to 9.68% 94.75% with 11.01% 97.5%,
      line to 4.1% 83.24%,
      curve to 4.18% 77.44% with 2.68% 80.32%,
      line to 41.91% 5.24%,
      curve to 46.44% 2.5% with 43.35% 2.5%,
      close
    );
  }
`;

const IconWrapper = styled(Vertical).attrs({ center: true })`
  position: absolute;
  justify-content: center;
  transform: rotate(var(--rotation));
`;

type Props = {
  borderColor?: string;
  background?: string;
  icon?: ReactNode;
  orientation?: ShieldShapeOrientation;
  size?: ShieldShapeSize;
  className?: string;
  style?: CSSProperties;
};

export const ShieldShape = ({
  borderColor = 'var(--c-nd-150)',
  background = 'var(--c-nl-800)',
  icon,
  orientation = 'down',
  size = 'md',
  className,
  style,
}: Props) => {
  const rotation = ORIENTATION_ROTATION[orientation];
  const sizePx = SIZE_PX[size];

  return (
    <Root
      className={classnames(className, {
        'shield-bottom-glow': !rotation,
      })}
      style={
        {
          '--rotation': `${rotation}deg`,
          '--shape-size': `${sizePx}px`,
          '--background': background,
          '--border': borderColor,
          ...style,
        } as CSSProperties
      }
    >
      <Inner />
      {!!icon && (
        <IconWrapper
          style={
            {
              top: '15%',
              bottom: 0,
              left: 0,
              right: 0,
              '--rotation': `${-rotation}deg`,
            } as CSSProperties
          }
        >
          {icon}
        </IconWrapper>
      )}
    </Root>
  );
};
